/**
 * SeriesPicker - Interstitial for radiologist-driven series selection
 *
 * Shown when there's no model-selected series (e.g., labeling, segmentation,
 * or demo cases without predictions). The radiologist clicks a series to load
 * it — only that series starts loading so it gets 100% of the network
 * bandwidth. Renders middle-slice thumbnails as they become available.
 *
 * Used by both CaseViewer and LabelingPage (DRY — single source of truth).
 */

import React, { useMemo } from "react";
import { useAtomValue } from "jotai";
import { SeriesMetadata, StudyMetadataResponse } from "../../utils/dicomMetadataLoader";
import { decideViewport } from "../../utils/pickViewportComponent";
import { gpuVerdictAtom } from "../../atoms/gpuVerdictAtom";
import { useSeriesThumbnails, SeriesThumbnail } from "../../hooks/useSeriesThumbnails";
import { BLOCKER_LABELS } from "../../utils/reconstructionBlocker";

export interface SeriesPickerProps {
  studyMetadata: StudyMetadataResponse;
  onSeriesSelect: (seriesId: string) => void;
  /** Optional label for context (e.g., "Select primary series for annotation") */
  title?: string;
}

interface SeriesOption {
  seriesId: string;
  meta: SeriesMetadata;
  instanceCount: number;
}

function formatPlane(plane: string | null): string {
  return plane ?? "Unknown";
}

function formatContrast(meta: SeriesMetadata): string {
  if (meta.has_contrast) {
    return meta.contrast_agent ? `+ ${meta.contrast_agent}` : "+ Contrast";
  }
  return "Pre-contrast";
}

/**
 * 60×60 thumbnail of a series's middle slice.
 *
 * The useSeriesThumbnails hook returns raw <canvas> elements. We serialize
 * them to data URLs and render as <img> — mirrors SeriesThumbnail.tsx so
 * React stays in full control of the DOM tree (no manual appendChild).
 */
const ThumbPreview: React.FC<{ thumb: SeriesThumbnail | undefined }> = ({ thumb }) => {
  const imageSrc = useMemo(() => {
    if (!thumb?.canvas) return undefined;
    try {
      return thumb.canvas.toDataURL();
    } catch {
      return undefined;
    }
  }, [thumb?.canvas]);

  return (
    <div className="w-[60px] h-[60px] rounded bg-surface-2 flex items-center justify-center shrink-0 overflow-hidden">
      {imageSrc ? (
        <img src={imageSrc} alt="" className="w-full h-full object-contain" draggable={false} />
      ) : (
        <span className="text-[9px] text-fg-muted">
          {thumb?.loading ? "…" : thumb?.error ? "—" : ""}
        </span>
      )}
    </div>
  );
};

export const SeriesPicker: React.FC<SeriesPickerProps> = ({
  studyMetadata,
  onSeriesSelect,
  title = "Select a series to load",
}) => {
  const gpuVerdict = useAtomValue(gpuVerdictAtom);
  const seriesOptions = useMemo((): SeriesOption[] => {
    if (!studyMetadata.series_metadata || !studyMetadata.series) return [];

    return Object.entries(studyMetadata.series_metadata)
      .map(([seriesId, meta]) => ({
        seriesId,
        meta,
        instanceCount: studyMetadata.series?.[seriesId]?.length ?? 0,
      }))
      .filter((s) => s.instanceCount > 0)
      .sort((a, b) => {
        // Non-reconstructable series (scouts, 2D, irregular spacing) sort to
        // the bottom — they remain visible + clickable (PR #439 gate) but
        // route to StackViewport, not MPR. Reconstructable axials first.
        const aRecon = decideViewport(a.meta, gpuVerdict).kind === "volume" ? 0 : 1;
        const bRecon = decideViewport(b.meta, gpuVerdict).kind === "volume" ? 0 : 1;
        if (aRecon !== bRecon) return aRecon - bRecon;
        const aAxial = a.meta.plane === "Axial" ? 0 : 1;
        const bAxial = b.meta.plane === "Axial" ? 0 : 1;
        if (aAxial !== bAxial) return aAxial - bAxial;
        return b.instanceCount - a.instanceCount;
      });
  }, [studyMetadata]);

  const { thumbnails } = useSeriesThumbnails(studyMetadata);
  const thumbBySeries = useMemo(() => {
    const m: Record<string, SeriesThumbnail> = {};
    for (const t of thumbnails) m[t.seriesId] = t;
    return m;
  }, [thumbnails]);

  return (
    <div className="w-full h-full bg-bg overflow-y-auto flex flex-col items-center p-8">
      <div className="max-w-2xl w-full my-auto">
        <h2 className="text-lg font-semibold text-fg mb-1 text-center">{title}</h2>
        <p className="text-sm text-fg-muted mb-6 text-center">
          Click a series to load. Only the selected series will load initially for faster display.
        </p>

        <div className="space-y-1.5">
          {seriesOptions.map((s) => {
            const recon = decideViewport(s.meta, gpuVerdict).kind === "volume";
            const blocker = s.meta.reconstruction_blocker;
            // Both routes are clickable. The viewport host (ProtocolViewport
            // or CaseViewer) picks StackViewport for non-reconstructable via
            // the shared decideViewport helper. Restored from PR #347;
            // dropped by #372 along with the StackViewport component.
            return (
              <button
                key={s.seriesId}
                onClick={() => onSeriesSelect(s.seriesId)}
                title={!recon && blocker ? BLOCKER_LABELS[blocker] : undefined}
                className={`
                  w-full text-left px-4 py-3 rounded-lg border transition-all duration-150 cursor-pointer
                  ${
                    recon
                      ? "border-border-strong bg-bg-2/80 hover:border-primary/50 hover:bg-surface-2/80"
                      : "border-border bg-bg-2/40 opacity-70 hover:opacity-90 hover:border-border-strong"
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <ThumbPreview thumb={thumbBySeries[s.seriesId]} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-fg truncate">
                      {s.meta.series_description || `Series ${s.meta.series_number}`}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-fg-muted">{formatPlane(s.meta.plane)}</span>
                      <span className="text-xs text-fg-muted">{formatContrast(s.meta)}</span>
                      {s.meta.slice_thickness && (
                        <span className="text-xs text-fg-muted">{s.meta.slice_thickness}mm</span>
                      )}
                      {!recon && blocker && (
                        <span className="text-xs text-fg-muted">
                          2D · {BLOCKER_LABELS[blocker] ?? blocker}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end ml-2 shrink-0">
                    <span className="text-xs text-fg-muted tabular-nums">
                      {s.instanceCount} slices
                    </span>
                    {/* Readers break equal-slice ties by Series Number, the order the
                        model is fed (device-engine #1083). Skip it when the title
                        already falls back to "Series N", and for a missing number
                        (stored as 0). */}
                    {s.meta.series_description && (s.meta.series_number ?? 0) > 0 && (
                      <span className="text-xs text-fg-muted tabular-nums">
                        Series {s.meta.series_number}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {seriesOptions.length === 0 && (
          <div className="text-center text-fg-muted py-8">No series available</div>
        )}
      </div>
    </div>
  );
};
